import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await login(email, password);
            toast.success("Bienvenido");
            const next = location.state?.from || "/dashboard";
            navigate(next, { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex flex-col bg-black text-white">
            <div className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl shadow-black/60" data-testid="login-card">
                    <div className="bg-slate-900 flex items-center justify-center py-10 px-6">
                        <img
                            src="/logo-emprenovus.png"
                            alt="Emprenovus"
                            className="w-56 h-56 object-contain"
                            data-testid="login-logo"
                        />
                    </div>
                    <form onSubmit={onSubmit} className="bg-white text-slate-900 p-8 space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-slate-700">Email Corporativo</Label>
                            <Input id="email" data-testid="login-form-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="correo@empresa.com" className="mt-1"/>
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="password"
                                    data-testid="login-form-password-input"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e)=>setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={()=>setShowPassword(s=>!s)}
                                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    data-testid="login-form-password-toggle"
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                                </button>
                            </div>
                        </div>
                        {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
                        <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={busy} data-testid="login-form-submit-button">
                            {busy ? "Ingresando..." : "Iniciar Sesión"}
                        </Button>

                        <div className="text-xs text-center text-slate-400 pt-1">
                            Acceso restringido a personal autorizado
                        </div>
                        <div className="text-xs text-center text-slate-500 pt-2 border-t border-slate-100">
                            <Link to="/forgot-password" className="hover:text-orange-600" data-testid="login-forgot-link">¿Olvidaste tu contraseña?</Link>
                        </div>
                    </form>
                </div>
            </div>
            <footer className="px-4 py-4 text-center text-xs text-slate-400">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                    <span>© 2026 Emprenovus. Todos los derechos reservados.</span>
                    <span className="text-slate-700">|</span>
                    <a href="https://www.emprenovus.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-orange-500 transition-colors" data-testid="footer-ai-link">
                        <img src="/favicon.png" alt="" className="w-4 h-4 object-contain" />
                        AI powered by Emprenovus
                    </a>
                </div>
            </footer>
        </div>
    );
}
